import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, ArrowRight, Send, Brain, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";

const SUGGESTIONS = [
  "What needs my attention today?",
  "Show me workforce gaps",
  "What leads should I follow up on?",
];
const TONE = {
  info: "border-blue-200 bg-blue-50 text-blue-800",
  warn: "border-amber-200 bg-amber-50 text-amber-800",
  good: "border-teal-200 bg-teal-50 text-teal-800",
};

export default function CommandCenter() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [q, setQ] = useState("");
  const [asked, setAsked] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
  }, [user, loading, navigate]);

  const run = async (prompt) => {
    setQ(prompt || "What needs my attention today?");
    setThinking(true); setAsked(true);
    try { const { data } = await api.get("/intelligence/briefing"); await new Promise((r) => setTimeout(r, 500)); setData(data); }
    catch { /* noop */ } finally { setThinking(false); }
  };

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  return (
    <div className="min-h-[70vh] bg-blue-950 text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center gap-2 mb-2"><Brain className="w-5 h-5 text-teal-400" /><p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold">T5 Intelligence · Command Center</p></div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold">Ask your care operation anything.</h1>

        <div className="mt-6 flex gap-2">
          <div className="flex-1 relative">
            <Sparkles className="w-4 h-4 text-teal-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run(q)} data-testid="cc-input"
              placeholder="What needs my attention today?" className="pl-9 bg-white/10 border-white/15 text-white placeholder:text-blue-200/60 h-12" />
          </div>
          <Button onClick={() => run(q)} data-testid="cc-ask" className="h-12 px-6 bg-teal-500 hover:bg-teal-600 text-white gap-2"><Send className="w-4 h-4" /> Ask</Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => run(s)} data-testid={`cc-suggestion-${s.length}`} className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-blue-100 transition-colors">{s}</button>
          ))}
        </div>

        {!asked && (
          <div className="mt-14 text-center text-blue-200/60"><Brain className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>Ask a question or tap a suggestion to get your live operational briefing.</p></div>
        )}

        {thinking && (
          <div className="mt-10 flex items-center gap-3 text-blue-100" data-testid="cc-thinking"><Loader2 className="w-5 h-5 animate-spin text-teal-400" /> T5 Intelligence is analyzing your operation…</div>
        )}

        {data && !thinking && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10" data-testid="cc-result">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex gap-3">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-teal-500/20 text-teal-400 shrink-0"><Sparkles className="w-5 h-5" /></span>
              <p className="text-blue-50 leading-relaxed" data-testid="cc-greeting">{data.greeting}</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5" data-testid="cc-cards">
              {data.cards.map((c) => (
                <div key={c.key} data-testid={`cc-card-${c.key}`} className="bg-white rounded-2xl p-4 text-blue-950">
                  <div className="flex items-start justify-between">
                    <p className="font-display text-3xl font-bold">{c.count}</p>
                    <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border ${TONE[c.tone]}`}>{c.tone === "good" ? "ready" : c.tone === "warn" ? "action" : "review"}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{c.label}</p>
                  <button onClick={() => navigate(c.action.link)} data-testid={`cc-action-${c.key}`} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-900 hover:gap-1.5 transition-all">{c.action.label} <ArrowRight className="w-4 h-4" /></button>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <p className="text-xs uppercase tracking-wide text-teal-400 font-semibold mb-2">Prepared actions — review &amp; approve</p>
              <div className="flex flex-wrap gap-2">
                {data.quick_actions.map((a) => (
                  <Button key={a.label} asChild variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10 gap-1.5" data-testid={`cc-quick-${a.label.replace(/\s/g, "")}`}>
                    <Link to={a.link}><CheckCircle2 className="w-4 h-4 text-teal-400" /> {a.label}</Link>
                  </Button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
